import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ArrowLeft, Loader2, Layers } from 'lucide-react';
import { presupuestoApi } from '../../utils/api';
import { dinero } from './formato';

/**
 * Presupuesto consolidado del grupo (P12): las empresas de la cuenta como una sola, por código de
 * cuenta, en una moneda. Lo que una empresa del grupo le factura a otra (por cédula) se elimina del
 * ingreso de una y del gasto de la otra; la utilidad del grupo es la suma de las utilidades. Con lo
 * real consolidado al lado.
 */
export default function Consolidado() {
  const navigate = useNavigate();
  const [anio, setAnio] = useState(new Date().getFullYear() + 1);
  const [moneda, setMoneda] = useState('CRC');
  const [opciones, setOpciones] = useState(null);
  const [elegidos, setElegidos] = useState({});
  const [r, setR] = useState(null);
  const [cargando, setCargando] = useState(false);
  // Con la moneda de lo que se muestra (mientras se consolida en otra, siguen los datos anteriores).
  const din = (n) => dinero(n, { moneda: r?.moneda || moneda, decimales: 0, cero: '–' });

  useEffect(() => {
    setOpciones(null); setR(null);
    presupuestoApi.consolidadoOpciones(anio).then(x => {
      const ops = x.data.data; setOpciones(ops);
      setElegidos(Object.fromEntries(ops.filter(e => e.sugerido).map(e => [e.id, e.sugerido])));
    }).catch(e => toast.error(e.response?.data?.error || 'No se pudieron cargar las empresas'));
  }, [anio]);
  useEffect(() => {
    const pares = Object.entries(elegidos).filter(([, p]) => p);
    if (pares.length < 2) { setR(null); return; }
    setCargando(true);
    presupuestoApi.consolidado({ anio, moneda, elegidos: pares.map(([e, p]) => `${e}:${p}`).join(',') })
      .then(x => setR(x.data.data)).catch(e => { toast.error(e.response?.data?.error || 'No se pudo consolidar'); setR(null); }).finally(() => setCargando(false));
  }, [elegidos, moneda, anio]);

  const TIPOS = [['ingreso', 'Ingresos', 'ingresos'], ['costo', 'Costos', 'costos'], ['gasto', 'Gastos', 'gastos']];
  // El nombre corto de cada empresa en la columna: la primera palabra, o las dos primeras si se repite.
  const corto = (nombre, todas) => { const w = nombre.replace(/,?\s*(S\.?R\.?L\.?|S\.?A\.?)$/i, '').split(/\s+/); const una = w[0]; return todas.filter(e => e.nombre.split(/\s+/)[0] === una).length > 1 ? w.slice(0, 2).join(' ') : una; };
  const th = 'bg-gray-50 text-right text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 px-3 py-2 border-b border-r border-gray-200 whitespace-nowrap';
  const td = 'px-3 py-1.5 border-b border-r border-gray-100 text-right font-mono text-[12.5px] whitespace-nowrap';
  return (
    <div className="space-y-4" data-prueba="consolidado">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate('/presupuesto')} className="text-gray-500 hover:text-gray-700" title="Volver"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Layers size={20} className="text-indigo-500" /> Presupuesto consolidado del grupo</h1>
          <p className="text-sm text-gray-500">Las empresas de la cuenta como una sola, sin lo que se facturan entre ellas.</p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <label htmlFor="cons-anio" className="text-gray-600">Año</label>
          <input id="cons-anio" type="number" value={anio} onChange={(e) => setAnio(Number(e.target.value))} className="w-24 border border-gray-300 rounded-lg px-2 py-1.5" />
          <label htmlFor="cons-moneda" className="text-gray-600">Moneda</label>
          <select id="cons-moneda" value={moneda} onChange={(e) => setMoneda(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 bg-white"><option value="CRC">Colones</option><option value="USD">Dólares</option></select>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-[10.5px] font-semibold tracking-wider uppercase text-gray-500 mb-2">Empresas y escenario de cada una</div>
        {!opciones ? <Loader2 className="animate-spin text-gray-400" /> : !opciones.length ? <p className="text-sm text-gray-500">Ninguna empresa tiene presupuesto de {anio}.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {opciones.map(e => (
              <label key={e.id} className={clsx('border rounded-lg p-3 text-sm', elegidos[e.id] ? 'border-indigo-300 bg-indigo-50/40' : 'border-gray-200')} data-prueba={`empresa-${e.id}`}>
                <div className="flex items-start gap-2">
                  <input type="checkbox" checked={!!elegidos[e.id]} onChange={(ev) => setElegidos({ ...elegidos, [e.id]: ev.target.checked ? (e.sugerido || e.presupuestos[0].id) : null })} className="mt-1" />
                  <div className="min-w-0 flex-1"><div className="font-medium text-gray-900 truncate" title={e.nombre}>{e.nombre}</div><div className="text-xs text-gray-500 font-mono">{e.identificacion}</div></div>
                </div>
                <select aria-label={`Escenario de ${e.nombre}`} disabled={!elegidos[e.id]} value={elegidos[e.id] || ''} onChange={(ev) => setElegidos({ ...elegidos, [e.id]: Number(ev.target.value) })} className="mt-2 w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white">
                  {e.presupuestos.map(p => <option key={p.id} value={p.id}>{p.escenario} · {p.moneda}{p.estado !== 'borrador' ? ` (${p.estado})` : ''}</option>)}
                </select>
              </label>
            ))}
          </div>
        )}
      </div>

      {cargando && <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 size={16} className="animate-spin" /> Consolidando…</div>}
      {r && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[['Ingresos del grupo', r.totales.consolidado.ingresos, r.totales.eliminacion.ingresos], ['Costos', r.totales.consolidado.costos, r.totales.eliminacion.costos], ['Gastos', r.totales.consolidado.gastos, r.totales.eliminacion.gastos], ['Utilidad del grupo', r.totales.consolidado.utilidad, null]].map(([t, v, el]) => (
              <div key={t} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
                <div className="text-[10.5px] font-semibold tracking-wider uppercase text-gray-500">{t}</div>
                <div className={clsx('text-xl font-bold font-mono mt-1', t.startsWith('Utilidad') && (v >= 0 ? 'text-emerald-700' : 'text-red-600'))}>{din(v)}</div>
                <div className="text-xs text-gray-500 mt-0.5">{el ? `Eliminado ${din(el)}` : `Suma de las empresas ${din(r.cuadre.sumaUtilidades)}`}</div>
              </div>
            ))}
          </div>

          {r.eliminaciones.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm space-y-2" data-prueba="eliminaciones">
              <div className="text-[10.5px] font-semibold tracking-wider uppercase text-gray-500">Eliminación intercompañía</div>
              {r.eliminaciones.map((el, i) => (
                <div key={i} className="flex flex-wrap gap-x-3 gap-y-1">
                  <b>{el.vende}</b><span className="text-gray-500">le factura a</span><b>{el.compra}</b><span className="font-mono">{din(el.total)}</span>
                  <span className="text-xs text-gray-500 w-full">Sale del ingreso de la que vende ({el.lineas.map(l => `${l.nombre}${l.cuenta ? ` ${l.cuenta}` : ''}`).join(', ')}) y del gasto de la que compra, en las cuentas donde asentó sus compras: {el.cuentasCompra.map(c => `${c.codigo} ${c.nombre} ${c.pct} %`).join(', ')}.</span>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
            <table className="border-separate border-spacing-0 w-full text-sm" style={{ minWidth: 340 + (r.empresas.length + 4) * 130 }}>
              <thead><tr>
                <th className={clsx(th.replace('text-right', 'text-left'), 'sticky left-0 z-10 min-w-[300px]')}>Cuenta</th>
                {r.empresas.map(e => <th key={e.emisorId} className={th} title={`${e.nombre} · ${e.presupuesto.escenario} · ${e.presupuesto.moneda}`}>{corto(e.nombre, r.empresas)}</th>)}
                <th className={th}>Suma</th><th className={th}>Eliminación</th><th className={clsx(th, 'bg-indigo-50 text-indigo-700')}>Consolidado</th>{r.conReal && <th className={th}>Real</th>}
              </tr></thead>
              {TIPOS.map(([t, titulo, k]) => {
                const fs = r.filas.filter(f => f.tipo === t);
                if (!fs.length) return null;
                return (
                  <tbody key={t}>
                    {fs.map(f => (
                      <tr key={f.codigo} data-prueba="fila-consolidado">
                        <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-b border-r border-gray-100 whitespace-nowrap"><span className="font-mono text-xs text-gray-500">{f.codigo.startsWith('—') ? '' : f.codigo}</span> {f.nombre}</td>
                        {r.empresas.map(e => <td key={e.emisorId} className={td}>{din(f.porEmpresa[e.emisorId])}</td>)}
                        <td className={td}>{din(f.suma)}</td>
                        <td className={clsx(td, f.eliminacion && 'text-amber-700')}>{din(f.eliminacion)}</td>
                        <td className={clsx(td, 'bg-indigo-50/50 text-indigo-800 font-semibold')}>{din(f.consolidado)}</td>
                        {r.conReal && <td className={clsx(td, 'text-gray-600')}>{din(f.real)}</td>}
                      </tr>
                    ))}
                    <tr className="font-semibold"><td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 border-b border-r border-gray-200">{titulo}</td>
                      {r.empresas.map(e => <td key={e.emisorId} className={clsx(td, 'bg-gray-50')}>{din(e[k])}</td>)}
                      <td className={clsx(td, 'bg-gray-50')}>{din(r.totales.suma[k])}</td><td className={clsx(td, 'bg-gray-50')}>{din(r.totales.eliminacion[k])}</td><td className={clsx(td, 'bg-indigo-50 text-indigo-800')}>{din(r.totales.consolidado[k])}</td>{r.conReal && <td className={clsx(td, 'bg-gray-50')}>{din(r.totales.real[k])}</td>}</tr>
                  </tbody>
                );
              })}
              <tfoot><tr className="font-semibold text-indigo-900" data-prueba="utilidad-consolidada"><td className="sticky left-0 z-10 bg-indigo-50 px-3 py-2 border-r border-gray-200">Utilidad</td>
                {r.empresas.map(e => <td key={e.emisorId} className={clsx(td, 'bg-indigo-50')}>{din(e.utilidad)}</td>)}
                <td className={clsx(td, 'bg-indigo-50')}>{din(r.totales.suma.utilidad)}</td><td className={clsx(td, 'bg-indigo-50')}>{din(r.totales.eliminacion.utilidad)}</td><td className={clsx(td, 'bg-indigo-100')}>{din(r.totales.consolidado.utilidad)}</td>{r.conReal && <td className={clsx(td, 'bg-indigo-50')}>{din(r.totales.real.utilidad)}</td>}</tr></tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
